import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { App, Button, Descriptions } from 'antd';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { ClearAllFiltersButton } from '@/components/data-table-actions';
import { API } from '@/services/api.service';
import { saveBlob } from './helpers';

/**
 * The table's header tools, as on the other lists: "Clear all filters"
 * resets every column filter and sort, "Excel" downloads the whole filtered
 * list. The filters themselves are on the columns.
 *
 * @param {Object} props
 * @param {string[]} props.keys - Address keys the columns write (`columnKeysOf`).
 * @param {Function} props.setQueryParams
 * @param {Object} [props.exportProps] - { company, kind, filters, labels, disabled }
 */
export function ListToolbar({ keys, setQueryParams, exportProps }) {
  const { t } = useTranslation(['sefState', 'tables']);
  const defaults = useMemo(() => Object.fromEntries(keys.map((k) => [k, undefined])), [keys]);

  return (
    <>
      <ClearAllFiltersButton
        label={t('clearAllFilters', { ns: 'tables' })}
        onReset={() => setQueryParams({ ...defaults, offset: 1 })}
        defaultParams={defaults}
      />
      {exportProps && <ExportButton {...exportProps} />}
    </>
  );
}

/** "Excel": the whole filtered list, headers in the user's language. */
export function ExportButton({ company, kind, filters, labels, disabled }) {
  const { t } = useTranslation('sefState');
  const { message } = App.useApp();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { blob, fileName } = await API.sefState.exportList(company, kind, filters, labels);
      saveBlob(blob, fileName);
    } catch {
      message.error(t('errors.export'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="default"
      className="btn-default h-8 text-black"
      icon={busy ? <LoadingOutlined className="text-zinc-400" spin /> : <DownloadOutlined className="text-zinc-400" />}
      disabled={disabled || busy}
      onClick={run}
    >
      {t('excel')}
    </Button>
  );
}

/** Sum line under a table. */
export function Sums({ items }) {
  return (
    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 px-1 text-sm">
      {items.filter(Boolean).map(([label, value]) => (
        <span key={label}>
          <span className="opacity-60">{label}: </span>
          <span className="font-semibold">{value}</span>
        </span>
      ))}
    </div>
  );
}

/** Label / value block of a detail panel. */
export function Facts({ items }) {
  return (
    <Descriptions
      size="small"
      column={1}
      bordered
      className="rounded-xl bg-white"
      items={items.filter(Boolean).map(([label, value]) => ({ key: label, label, children: value ?? '—' }))}
    />
  );
}
